/* eslint-disable complexity */
import { isEqual, omit, sortBy, uniqBy, groupBy, flow, map } from 'lodash';

const initialState = {
  savedExternalIndicator: {},
  externalDataIndicatorUpdates: {},
  configuredDataSets: [],
  customIndicatorTypeDetails: {},
  existingCustomIndicatorTypeDetails: {},
  // district or school UUID
  selectedPlanScope: undefined,
  selectedSchoolYear: undefined,
  additionalInfoChanged: false,
  planLoading: undefined,
  activeGrades: [],
  initialActiveGrades: [],
  previouslySavedIndicatorNames: [],
  context: {
    categories: [],
    // {
    //   id, -> create identifier type need this id for category field
    //   name,
    // }
    planScopes: [],
    // {
    //    name, -> school name
    //    scopeName, -> only shows up when it's district scope
    //    id, -> school id
    //    school/indicatorTypeCount,
    //    customizedSchoolIndicatorsCount,
    // }
    currentSchoolYear: undefined,
    yearsToShow: [],
    // integer value of year
    customIndicatorTypes: [],
    // {
    //   [userType]: [`Staff Indicator`, `Custom Activity`, `Custom To Do`],
    // }
    // Staff Indicator -> valueType customInstructions -> * if valueType is boolean, then need configuration: {positiveDirection, count} be passed
    // Custom Activity -> customInstructions, activityTask(created from learning task creation)
    // Custom To Do -> customInstructions
    //
    responsibleParties: [],
    // `student`, `k12_admin`, `guardian`
    // use camelCase of this to get the indicator types from customIndicatorTypes
    analyticsBreakoutOptions: [],
    districtSchoolYearTransitionDate: undefined,
    valueTypes: [],
    // boolean, count,
    indicatorTypeInstructions: [],
    //    "Staff Indicator": [
    //        "value_type",
    //        "custom_instructions"
    //    ],
    //    "Custom To Do": [
    //        "custom_instructions"
    //    ],
    //    "Custom Activity": [
    //        "activity_task",
    //        "custom_instructions"
    //    ]
  },
  currentPlan: {
    categories: [],
  },
  // categories:
  // {
  //  category: "Category name",
  //  indicatorTypes: [
  //    {
  //      uuid,
  //      created,
  //      name,
  //      customInstructions, -> description of the type indicators?
  //      type,
  //      valueType,
  //      category,
  //      responsibleParty,
  //      schoolSpecific,
  //      videoFile,
  //      isToDoIndicatorAllowed,
  //      criteriaTemplate,
  //      slSuggestedGrades,
  //      grades: [
  //        {
  //            "grade": 6,
  //            "isPlanned": true,
  //            "plannedAt": "District",
  //            "isConfigured": true,
  //            "configuredAt": null,
  //            "isActivated": false,
  //            "indicator": null,
  //            "studentIndicators": {}
  //        },
  //      ],
  //    }
  //  ]
  // }
  futurePlan: {
    categories: [],
  },
  // is used to represent the timeline view
  indicators: [],
  // used to render the default activated indicator type in timeline view
  viewIndicatorType: undefined,
  // render edit indicator type in config drawer
  editIndicatorType: undefined,
  // Config page v2 view by grade mode viewing grade, -1 is all grades
  viewGrade: -1,
  // selected editing indicator, to render selected status in view by grade mode
  editIndicator: undefined,
  // used to show available grades when hovering on key readiness indicator icon in grid view
  hoveringKeyReadinessIndicatorTypeUuid: undefined,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case 'INDICATOR_CONFIG_V2.SET_CUSTOM_INDICATOR_TYPE_DETAILS': {
      const details = state.customIndicatorTypeDetails;
      const fieldsToObserve = [
        'name',
        'category',
        'customInstructions',
        'timeToComplete',
        'elements',
        'survey',
      ];
      const fieldHasChanged = action?.skipChangedCheck
        ? false
        : fieldsToObserve.find(
            field =>
              !action.details.uuid &&
              action.details[field] &&
              !isEqual(details?.[field], action.details[field]),
          );
      return {
        ...state,
        additionalInfoChanged: fieldHasChanged
          ? true
          : state.additionalInfoChanged,
        customIndicatorTypeDetails: {
          ...state.customIndicatorTypeDetails,
          ...action.details,
        },
      };
    }
    case 'INDICATOR_CONFIG_V2.SET_EXISTING_CUSTOM_INDICATOR_TYPE_DETAILS': {
      return {
        ...state,
        existingCustomIndicatorTypeDetails: state.customIndicatorTypeDetails,
      };
    }
    case 'INDICATOR_CONFIG_V2.RESET_ADDITIONAL_INFO_CHANGED':
      return {
        ...state,
        additionalInfoChanged: false,
        externalDataIndicatorUpdates: {},
      };
    case 'INDICATOR_CONFIG_V2.RESET_CUSTOM_INDICATOR_TYPE_DETAILS':
      return {
        ...state,
        customIndicatorTypeDetails: {},
      };
    case 'INDICATOR_CONFIG_V2.POPULATE_CONTEXT':
      return {
        ...state,
        context: action.context,
        selectedSchoolYear:
          state.selectedSchoolYear || action.context.currentSchoolYear,
      };
    case 'INDICATOR_CONFIG_V2.POPULATE_PLAN':
      return {
        ...state,
        [action.planType]: {
          ...state[action.planType],
          categories: flow([
            values => sortBy(values, 'category.order'),
            values => groupBy(values, 'category.name'),
            values =>
              map(values, (value, key) => ({
                category: key,
                id: value[0].category.id,
                indicatorTypes: value.map(indicatorType => ({
                  ...indicatorType,
                  grades: indicatorType.indicators.map(indicator => ({
                    grade: indicator.gradeYear,
                    isPlanned: true,
                    plannedAt: indicator.plannedAt,
                    isConfigured: !!indicator.configuredAt,
                    configuredAt: indicator.configuredAt,
                    isActivated: !!indicator.activatedAt,
                    activatedAt: indicator.activatedAt,
                    indicator,
                    districtIndicator: indicator.districtIndicator,
                  })),
                })),
              })),
          ])(action.plan),
          indicatorTypes: action.plan,
        },
      };
    case 'INDICATOR_CONFIG_V2.POPULATE_SINGLE_INDICATOR_TYPE':
      return {
        ...state,
        [action.planType]: {
          ...state[action.planType],
          categories: state[action.planType].categories.map(category => ({
            ...category,
            indicatorTypes: category.indicatorTypes.map(indicatorType => {
              if (indicatorType.uuid === action.indicatorType.uuid) {
                return action.indicatorType;
              }
              return indicatorType;
            }),
          })),
        },
      };
    case 'INDICATOR_CONFIG_V2.SET_PLAN_SCOPE':
      return {
        ...state,
        selectedPlanScope: action.uuid,
      };
    case 'INDICATOR_CONFIG_V2.UPDATE_INDICATORS':
      return {
        ...state,
        [action.planType]: {
          ...state[action.planType],
          categories: state[action.planType].categories.map(category => ({
            ...category,
            indicatorTypes: category.indicatorTypes.map(indicatorType => {
              if (indicatorType.uuid === action.indicatorTypeUuid) {
                const indicators = sortBy(
                  uniqBy(
                    [...action.indicators, ...indicatorType.indicators],
                    'gradeYear',
                  ),
                  'gradeYear',
                );

                return {
                  ...indicatorType,
                  indicators,
                  grades: indicators.map(indicator => ({
                    grade: indicator.gradeYear,
                    isPlanned: true,
                    plannedAt: indicator.plannedAt,
                    isConfigured: !!indicator.configuredAt,
                    configuredAt: indicator.configuredAt,
                    isActivated: !!indicator.activatedAt,
                    activatedAt: indicator.activatedAt,
                    indicator,
                    districtIndicator: indicator.districtIndicator,
                  })),
                };
              }
              return indicatorType;
            }),
          })),
        },
      };
    case 'INDICATOR_CONFIG_V2.POPULATE_SINGLE_INDICATOR':
      return {
        ...state,
        [action.planType]: {
          ...state[action.planType],
          categories: state[action.planType].categories.map(category => ({
            ...category,
            indicatorTypes: category.indicatorTypes.map(indicatorType => {
              const indicatorMatch =
                indicatorType.indicators.filter(
                  i => i.uuid === action.indicator.uuid,
                )?.length > 0;
              if (!indicatorMatch) {
                return indicatorType;
              }
              const indicators = sortBy(
                uniqBy(
                  [action.indicator, ...indicatorType.indicators],
                  'gradeYear',
                ),
                'gradeYear',
              );

              return {
                ...indicatorType,
                indicators,
                grades: indicators.map(indicator => ({
                  grade: indicator.gradeYear,
                  isPlanned: true,
                  plannedAt: indicator.plannedAt,
                  isConfigured: !!indicator.configuredAt,
                  configuredAt: indicator.configuredAt,
                  isActivated: !!indicator.activatedAt,
                  activatedAt: indicator.activatedAt,
                  indicator,
                  districtIndicator: indicator.districtIndicator,
                })),
              };
            }),
          })),
        },
      };
    case 'INDICATOR_CONFIG_V2.POPULATE_INDICATORS':
      return {
        ...state,
        indicators: action.indicators.map(indicator => ({
          ...indicator,
          isActivated: indicator.status === 'activated',
        })),
      };
    case 'INDICATOR_CONFIG_V2.SET_VIEW_INDICATOR_TYPE':
      return {
        ...state,
        viewIndicatorType: action.indicatorType,
      };
    case 'INDICATOR_CONFIG_V2.SET_EDIT_INDICATOR_TYPE':
      return {
        ...state,
        editIndicatorType: action.indicatorType,
      };
    case 'INDICATOR_CONFIG_V2.SET_EDIT_INDICATOR':
      return {
        ...state,
        editIndicator: action.indicator,
      };
    case 'INDICATOR_CONFIG_V2.SET_PLAN_LOADING':
      return {
        ...state,
        planLoading: action.status,
      };
    case 'INDICATOR_CONFIG_V2.UPDATE_ACTIVE_GRADES':
      return {
        ...state,
        activeGrades: action.activeGrades,
      };
    case 'INDICATOR_CONFIG_V2.UPDATE_INITIAL_ACTIVE_GRADES':
      return {
        ...state,
        initialActiveGrades: action.initialActiveGrades,
      };
    case 'INDICATOR_CONFIG_V2.UPDATE_EXTERNAL_DATA_INDICATOR':
      return {
        ...state,
        externalDataIndicatorUpdates: {
          ...state.externalDataIndicatorUpdates,
          ...action.updates,
        },
      };
    case 'INDICATOR_CONFIG_V2.SET_SAVED_EXTERNAL_DATA_INDICATOR_DETAILS':
      return {
        ...state,
        externalDataIndicatorUpdates: {},
        savedExternalIndicator: action.details,
      };
    case 'INDICATOR_CONFIG_V2.RESET_EXTERNAL_DATA_INDICATOR':
      return {
        ...state,
        savedExternalIndicator: {},
        externalDataIndicatorUpdates: {},
      };
    case 'INDICATOR_CONFIG_V2.POPULATE_CONFIGURED_DATA_SETS':
      return {
        ...state,
        configuredDataSets: action.datasets,
      };
    case 'INDICATOR_CONFIG_V2.SET_SCHOOL_YEAR':
      return {
        ...state,
        selectedSchoolYear: action.year,
      };
    case 'INDICATOR_CONFIG_V2.POPULATE_INDICATOR_NAMES':
      return {
        ...state,
        previouslySavedIndicatorNames: action.names,
      };
    case 'INDICATOR_CONFIG_V2.SET_VIEW_GRADE':
      return {
        ...state,
        viewGrade: action.grade,
      };
    case 'INDICATOR_CONFIG_V2.BULK_REMOVE_INDICATORS':
      return {
        ...state,
        activeGrades: [],
        // Remove grades from currentPlan or futurePlan
        ...{
          [action.planType]: {
            ...state[action.planType],
            categories: state[action.planType].categories.map(category => {
              if (
                category.category !==
                  (action?.editIndicatorType?.category?.name ||
                    action.editIndicator?.indicatorType?.category?.name) &&
                (!!action?.editIndicatorType?.category?.name ||
                  !!action.editIndicator?.indicatorType?.category?.name)
              )
                return category;
              return {
                ...category,
                indicatorTypes: category.indicatorTypes.map(indicatorType => {
                  if (indicatorType.uuid !== action.indicatorTypeUuid)
                    return indicatorType;
                  return {
                    ...omit(indicatorType, ['grades', 'indicators']),
                    grades: indicatorType.grades.filter(
                      gradeInfo => !action.grades.includes(gradeInfo.grade),
                    ),
                    indicators: indicatorType.indicators.filter(
                      indicator => !action.grades.includes(indicator.gradeYear),
                    ),
                  };
                }),
              };
            }),
          },
        },
        // Remove grades from editing indicator type in grid mode
        editIndicatorType: action.editIndicatorType?.uuid
          ? {
              ...action.editIndicatorType,
              grades: action.editIndicatorType.grades.filter(
                gradeInfo => !action.grades.includes(gradeInfo.grade),
              ),
              indicators: action.editIndicatorType.indicators.filter(
                indicator => !action.grades.includes(indicator.gradeYear),
              ),
            }
          : action.editIndicatorType,
        // Remove grades from editing indicator in view by grade mode
        editIndicator: action.editIndicator?.id
          ? {
              ...action.editIndicator,
              indicatorType: {
                ...action.editIndicator.indicatorType,
                grades: action.editIndicator.indicatorType.grades.filter(
                  gradeInfo => !action.grades.includes(gradeInfo.grade),
                ),
              },
            }
          : action.editIndicator,
      };
    case 'INDICATOR_CONFIG_V2.ADD_INDICATOR':
      return {
        ...state,
        activeGrades: [],
        // Add grade to currentPlan or futurePlan
        // ...{
        //   [action.planType]: {
        //     ...state[action.planType],
        //     categories: state[action.planType].categories.map(
        //       (category, categoryIndex) => {
        //         if (
        //           category.category !==
        //             (action?.editIndicatorType?.category?.name ||
        //               action.editIndicator?.indicatorType?.category?.name) &&
        //           (!!action?.editIndicatorType?.category?.name ||
        //             !!action.editIndicator?.indicatorType?.category?.name)
        //         )
        //           return category;
        //         return {
        //           ...category,
        //           indicatorTypes: category.indicatorTypes.map(
        //             (indicatorType, indicatorTypeIndex) => {
        //               if (indicatorType.uuid !== action.indicatorTypeUuid)
        //                 return indicatorType;
        //               return {
        //                 ...omit(indicatorType, ['grades', 'indicators']),
        //                 grades: [
        //                   ...indicatorType.grades.filter(
        //                     g => g.grade !== action.grade,
        //                   ),
        //                   ...(action?.dataFromDB?.length
        //                     ? [
        //                         action.dataFromDB[categoryIndex][
        //                           indicatorTypeIndex
        //                         ].grades.find(g => g.grade === action.grade),
        //                       ]
        //                     : [
        //                         {
        //                           grade: action.grade,
        //                           activatedAt: null,
        //                           configuredAt: null,
        //                           districtIndicator: null,
        //                           indicator: {
        //                             schoolYear: action.data?.schoolYear,
        //                             gradeYear: action.grade,
        //                             configuration: {},
        //                             dueDate: null,
        //                             isToDo: null,
        //                             status: 'planned',
        //                             plannedAt: action.data?.school
        //                               ? 'School'
        //                               : 'District',
        //                             configuredAt: null,
        //                             activatedAt: null,
        //                             school: null,
        //                             districtIndicator: null,
        //                           },
        //                           isActivated: false,
        //                           isConfigured: false,
        //                           isPlanned: true,
        //                           plannedAt: action.data?.school
        //                             ? 'School'
        //                             : 'District',
        //                         },
        //                       ]),
        //                 ],
        //                 indicators: [
        //                   ...indicatorType.indicators.filter(
        //                     g => g.gradeYear !== action.grade,
        //                   ),
        //                   ...(action?.dataFromDB?.length
        //                     ? [
        //                         action.dataFromDB[categoryIndex][
        //                           indicatorTypeIndex
        //                         ].indicators.find(
        //                           g => g.gradeYear === action.grade,
        //                         ),
        //                       ]
        //                     : [
        //                         {
        //                           gradeYear: action.grade,
        //                           schoolYear: action.data?.schoolYear,
        //                           configuration: {},
        //                           dueDate: null,
        //                           isToDo: null,
        //                           status: 'planned',
        //                           plannedAt: action.data?.school
        //                             ? 'School'
        //                             : 'District',
        //                           configuredAt: null,
        //                           activatedAt: null,
        //                           school: null,
        //                           districtIndicator: null,
        //                         },
        //                       ]),
        //                 ],
        //               };
        //             },
        //           ),
        //         };
        //       },
        //     ),
        //   },
        // },
        [action.planType]: {
          ...state[action.planType],
          categoryIndicatorTypeGradeMap: {
            ...state[action.planType].categoryIndicatorTypeGradeMap,
            [`${action.categoryId}_${action.indicatorTypeUuid}_${action.grade}`]:
              {
                grade: action.grade,
                activatedAt: null,
                configuredAt: null,
                districtIndicator: null,
                indicator: {
                  schoolYear: action.data?.schoolYear,
                  gradeYear: action.grade,
                  configuration: {},
                  dueDate: null,
                  isToDo: null,
                  status: 'planned',
                  plannedAt: action.data?.school ? 'School' : 'District',
                  configuredAt: null,
                  activatedAt: null,
                  school: null,
                  districtIndicator: null,
                },
                isActivated: false,
                isConfigured: false,
                isPlanned: true,
                plannedAt: action.data?.school ? 'School' : 'District',
              },
          },
        },
      };
    default:
      return state;
  }
};
